import React from 'react';
import { Box, IconButton, type SxProps, type Theme } from '@mui/material';
import { Check } from 'lucide-react';

export interface InlineEditFieldProps {
  /** Current value. */
  value: string;
  /** Called on every keystroke. */
  onChange: (value: string) => void;
  /** Commit the edit (Enter key or confirm button). */
  onConfirm: () => void;
  /** Abandon the edit (Escape key or blur). */
  onCancel: () => void;
  /** Focus the input on mount. Defaults to true. */
  autoFocus?: boolean;
  placeholder?: string;
  /** aria-label for the confirm button. */
  confirmLabel?: string;
  /** Confirm-button icon; defaults to a check. */
  confirmIcon?: React.ReactNode;
  /** MUI sx overrides for the pill container (merged last). */
  sx?: SxProps<Theme>;
}

/**
 * InlineEditField
 *
 * Atomic level: Molecule (borderless input + confirm button in a pill).
 *
 * A compact, pill-shaped inline text editor: type, then Enter / the check button
 * commits, and Escape or blurring cancels. The confirm button preserves input
 * focus on mouse-down so clicking it commits instead of triggering a blur-cancel.
 * Zero domain knowledge.
 */
export function InlineEditField({
  value,
  onChange,
  onConfirm,
  onCancel,
  autoFocus = true,
  placeholder,
  confirmLabel = 'Confirm',
  confirmIcon,
  sx,
}: InlineEditFieldProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: '2rem',
        boxSizing: 'border-box',
        borderRadius: '100vh',
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        pl: '12px',
        pr: '6px',
        ...sx,
      }}
    >
      <Box
        component="input"
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
        onBlur={onCancel}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            onConfirm();
          }
          if (event.key === 'Escape') {
            onCancel();
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          p: 0,
          border: 'none',
          outline: 'none',
          backgroundColor: 'transparent',
          fontFamily: 'inherit',
          fontSize: '0.875rem',
          color: '#0F1729',
        }}
      />
      <IconButton
        aria-label={confirmLabel}
        disableRipple
        onMouseDown={event => event.preventDefault()}
        onClick={onConfirm}
        sx={{
          flexShrink: 0,
          width: '1.5rem',
          height: '1.5rem',
          p: 0,
          color: '#0F1729',
          borderRadius: '100vh',
          '&:hover': { backgroundColor: '#f1f2f4' },
        }}
      >
        {confirmIcon ?? <Check size={13} />}
      </IconButton>
    </Box>
  );
}

export default InlineEditField;
